'use strict';

import extend from 'lodash/extend';

angular.module('atex.aceui.service').factory('ImageService',
  ['$q', '$rootScope', 'AuthenticationService', 'ApplicationConfigurationService',

   function($q, $rootScope, AuthenticationService, ApplicationConfigurationService) {
     var serviceHandler = undefined;

     var handler = function() {
       if (serviceHandler !== undefined) {
         return $q.when(serviceHandler);
       }

       return ApplicationConfigurationService.getConfigurationFor('imageServiceUrl').then(function(imageServiceUrl) {
         if (imageServiceUrl !== undefined) {
           return imageServiceUrl;
         }

         return ApplicationConfigurationService.getServiceConfigurationFor('ImageService').then(function(configuration) {
           return atex.aceui.ServiceUtils.calculateServiceUrl(configuration, '/image');
         });
       }).then(function(serviceUrl) {
         var serviceSettings = { customQ: atex.aceui.AngularUtils.applyingQ($q, $rootScope) };
         serviceHandler = atex.aceui.service.ImageService(serviceUrl, serviceSettings);

         return serviceHandler;
       });
     };

     return {
       /**
        * Returns an image service URL for the image content and image file represented by the given input object.
        * Wraps the request with an authentication guard.
        *
        * @param {Object} transferInfo - a transfer info object containing request details
        * @param {string} transferInfo.alias - the alias of the image content to create the URL for
        * @param {string} transferInfo.fileName - the file name of the image file on the image content
        * @param {string} transferInfo.width - the requested width of the image, in pixels
        * @param {string} transferInfo.height - the requested height of the image, in pixels
        * @param {string} transferInfo.mode - the requested resize mode to use (f.e. 'FIT') for the image
        * @param {string} transferInfo.crop - the requested crop rectangle for the image
        * @param {string} transferInfo.aspectRatio - the requested aspect ratio for the image
        * @param {string} transferInfo.format - the name for the request format for the image
        * @param {string} transferInfo.quality - the requested quality for the image
        * @param {boolean} transferInfo.skipToken - do not provide a token parameter in resulting url
        *
        * @see atex.aceui.service.ImageService
        * @see AuthenticationService
        *
        * @returns {Promise.<{string}, atex.aceui.error.Error>} - the image URL
        */
       getImageUrl: function(transferInfo) {
         return AuthenticationService.callWithAuthentication(function(currentUser) {
           return handler().then(function(serviceHandler) {
             let options = transferInfo.skipToken ? transferInfo : extend(transferInfo, { authToken: currentUser.token });

             return serviceHandler.getImageUrl(options);
           });
         });
       },

       /**
        * Retrieves an image from the Image service and returns it as a
        * blob (as defined by https://developer.mozilla.org/en-US/docs/Web/API/Blob).
        *
        * Wraps the request with an authentication guard.
        *
        * @param {Object} transferInfo - a transfer info object containing request details
        * @param {string} transferInfo.alias - the alias of the image content of the image to retrieve. Mutually exclusive with transferInfo.fileUri
        * @param {string} transferInfo.fileUri - the file service URI of the image to retrieve. Mutually exclusive with transferInfo.alias
        * @param {string} transferInfo.fileName - the file name of the image file on the image content. Only applicable if transferInfo.alias is specified
        * @param {string} transferInfo.width - the requested width of the image, in pixels
        * @param {string} transferInfo.height - the requested height of the image, in pixels
        * @param {string} transferInfo.mode - the requested resize mode to use (f.e. 'FIT') for the image
        * @param {string} transferInfo.crop - the requested crop rectangle for the image
        * @param {string} transferInfo.aspectRatio - the requested aspect ratio for the image
        * @param {string} transferInfo.format - the name for the request format for the image
        * @param {string} transferInfo.quality - the requested quality for the image
        * @param {string} transferInfo.imageMimeType - the requested image mime type for the image
        *
        * @see atex.aceui.service.ImageService
        * @see AuthenticationService
        *
        * @returns {Promise.<{BLOB}, atex.aceui.error.Error>} - the image, as a BLOB
        */
       getImage: function(transferInfo) {
         return AuthenticationService.callWithAuthentication(function(currentUser) {
           return handler().then(function(serviceHandler) {
             return serviceHandler.getImage(_.extend(transferInfo, { authToken: currentUser.token }));
           });
         });
       }
     };
   }
  ]);
